import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  PendingConflictsSnapshot,
  ConflictsResolveWithAiResult,
} from '@volley/crucible-ipc-types/ipc-channels';
import { useCrucibleClient } from '../transport/context';
import { projectQueryKey } from '../transport/project-query-key';

/**
 * Crucible #592 — React Query hooks for the `conflicts:*` IPC family.
 *
 *  - `usePendingConflicts(gameId)` — polls `conflicts:get-pending` so the
 *    `<ConflictResolutionCard>` mounts as soon as `crucible update`
 *    writes `.foundry/pending-conflicts.json` and unmounts when main
 *    deletes it (Resolve-with-AI completion or Resolve-manually
 *    dismiss). Polling cadence mirrors `useBugDraft` from
 *    `useBugDraft.ts` — short `staleTime` + explicit `refetchInterval`
 *    so a producer-side write surfaces in <2s.
 *
 *  - `useResolveWithAi` / `useDismissConflicts` — imperative IPC
 *    wrappers used by the card's buttons. Both invalidate the
 *    pending-conflicts query on completion so a stale card disappears
 *    immediately rather than waiting for the next poll tick.
 */

const POLL_OPTIONS = {
  staleTime: 1500,
  refetchInterval: 2000,
  refetchOnMount: 'always',
  refetchOnWindowFocus: 'always',
  retry: false,
} as const;

export function usePendingConflicts(
  gameId: string | undefined,
  options: { enabled?: boolean } = {},
): {
  data: PendingConflictsSnapshot | null | undefined;
  isLoading: boolean;
  refetch: () => void;
} {
  const client = useCrucibleClient();
  const query = useQuery<PendingConflictsSnapshot | null>({
    queryKey: projectQueryKey(client, ['conflicts:get-pending', gameId]),
    queryFn: async () => {
      if (!gameId) throw new Error('gameId is required');
      return await client.invoke('conflicts:get-pending', { gameId });
    },
    enabled: (options.enabled ?? true) && typeof gameId === 'string' && gameId.length > 0,
    ...POLL_OPTIONS,
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    refetch: () => {
      void query.refetch();
    },
  };
}

export function useResolveWithAi(): {
  resolveWithAi: (args: {
    gameId: string;
    files: string[];
  }) => Promise<ConflictsResolveWithAiResult>;
} {
  const queryClient = useQueryClient();
  const client = useCrucibleClient();
  // PR #600 round-1 Bugbot LOW: wrap in `useCallback` so the returned
  // function reference is stable across renders. Without this, every
  // render of `ConflictResolutionCard` creates a fresh `resolveWithAi`
  // ref, which is then a dep of the card's `handleResolveWithAi`
  // useCallback — making that memoization a no-op. `queryClient` is
  // stable across renders (held by QueryClientProvider), so the
  // resulting callback identity only changes when the provider does.
  const resolveWithAi = useCallback(
    async (args: { gameId: string; files: string[] }) => {
      const result = await client.invoke('conflicts:resolve-with-ai', args);
      // We do NOT invalidate on success — the sidecar's still on
      // disk until the agent finishes resolving every file (main
      // drives deletion via the PostToolBatch cleanup hook). The
      // 2s poll will pick up the disappearance on its own.
      //
      // On error we invalidate so a transient "no-sidecar" race
      // refreshes the card state immediately.
      if (!result.ok) {
        void queryClient.invalidateQueries({
          queryKey: projectQueryKey(client, ['conflicts:get-pending', args.gameId]),
        });
      }
      return result;
    },
    [client, queryClient],
  );
  return { resolveWithAi };
}

export function useDismissConflicts(): {
  dismissConflicts: (gameId: string) => Promise<{ ok: boolean }>;
} {
  const queryClient = useQueryClient();
  const client = useCrucibleClient();
  // PR #600 round-1 Bugbot LOW: same reason as `useResolveWithAi`
  // above — stabilise the function identity so the card's `useCallback`
  // memoization actually works.
  const dismissConflicts = useCallback(
    async (gameId: string) => {
      const result = await client.invoke('conflicts:dismiss', { gameId });
      void queryClient.invalidateQueries({
        queryKey: projectQueryKey(client, ['conflicts:get-pending', gameId]),
      });
      return result;
    },
    [client, queryClient],
  );
  return { dismissConflicts };
}
